import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminLogin = ({ onLogin }) => {
  const { toast } = useToast();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create base64 encoded credentials for HTTP Basic Auth
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Store auth in localStorage (in production, use more secure method)
        localStorage.setItem('admin_auth', auth);
        
        toast({
          title: "התחברות בוצעה בהצלחה!",
          description: "ברוך הבא למערכת הניהול",
        });
        
        onLogin(auth);
      } else {
        toast({
          title: "שגיאה בהתחברות",
          description: "שם משתמש או סיסמה שגויים",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "שגיאת חיבור",
        description: "בעיה בחיבור לשרת",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900/50 backdrop-blur-md border-amber-500/20">
        <CardHeader className="text-center pb-2">
          {/* Admin Login Logo */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative mb-4">
              {/* Modern Admin Logo */}
              <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 w-20 h-20 rounded-3xl shadow-2xl flex items-center justify-center relative">
                <div className="text-black font-black text-3xl">J</div>
                {/* Admin badge */}
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-sm font-bold rounded-full w-7 h-7 flex items-center justify-center border-2 border-white shadow-lg">
                  A
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-xl font-black mb-1">
                <span className="bg-gradient-to-r from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
                  J CLASS MOTORS
                </span>
              </div>
              <div className="text-xs text-amber-400 font-bold tracking-[0.2em] mb-4">
                ADMIN PANEL
              </div>
            </div>
          </div>
          
          <CardTitle className="text-2xl font-bold text-white">
            מערכת ניהול
          </CardTitle>
          <p className="text-gray-300">התחבר למערכת הניהול של J CLASS MOTORS</p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white">שם משתמש</Label>
              <div className="relative">
                <User className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({
                    ...prev,
                    username: e.target.value
                  }))}
                  className="pr-10 bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500"
                  placeholder="admin"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">סיסמה</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({
                    ...prev,
                    password: e.target.value
                  }))}
                  className="pr-10 pl-10 bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-amber-500"
                  placeholder="הכנס סיסמה"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-3 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-3 transform hover:scale-105 transition-all duration-300 disabled:opacity-50"
            >
              {isLoading ? "מתחבר..." : "התחבר"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;